import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowRight } from 'lucide-react';
import { api, useAuth } from '../App';
import { Button } from '../components/ui/button';
import TwoFactorSettingsCard from '../components/account/TwoFactorSettingsCard';

/**
 * Dedicated 2FA setup landing page.
 *
 * Reached in two ways:
 *   1. An admin logging in with 2FA not yet enrolled, Login.js redirects them
 *      here (`must_setup_2fa` flag on the login response). They're already
 *      authenticated via the session cookie; they just can't proceed until
 *      they finish enrolment.
 *   2. Any user who wants to enrol explicitly can be linked here.
 *
 * Once `two_factor_enabled` flips true we drop them into their dashboard.
 */
export default function TwoFactorSetup() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/auth/2fa/status');
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Poll while the user is enrolling so the "Continue" CTA appears the moment
  // the status flips.
  useEffect(() => {
    if (status?.enabled) return;
    const t = setInterval(loadStatus, 4000);
    return () => clearInterval(t);
  }, [status?.enabled, loadStatus]);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login', { replace: true });
  }, [authLoading, user, navigate]);

  const goToDashboard = () => {
    if (user?.role === 'admin') navigate('/admin/z-control', { replace: true });
    else if (user?.role === 'provider') navigate('/provider/dashboard', { replace: true });
    else navigate('/dashboard', { replace: true });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  const mandatory = !!status?.required_by_role;
  const enabled = !!status?.enabled;

  return (
    <div className="min-h-screen bg-[#F9F6F0] py-12 px-4" data-testid="two-factor-setup-page">
      <div className="max-w-xl mx-auto">
        <div className="card-surface p-8 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-medium text-[#1A1A1A]">
                {mandatory
                  ? 'Admin security, set up two-factor authentication'
                  : 'Set up two-factor authentication'}
              </h1>
              <p className="text-sm text-stone-500 mt-2 leading-relaxed">
                {mandatory ? (
                  <>Your role requires two-factor authentication. To keep the platform secure, enable it now to continue, you'll be asked for a 6-digit code every time you sign in.</>
                ) : (
                  <>Add an extra layer of security to your account. You'll be asked for a 6-digit code (emailed to you) whenever you sign in.</>
                )}
              </p>
            </div>
          </div>
        </div>

        <TwoFactorSettingsCard />

        <div className="mt-6 flex justify-end">
          <Button
            onClick={goToDashboard}
            disabled={mandatory && !enabled}
            className="bg-[#1A1A1A] hover:bg-black text-white gap-1"
            data-testid="two-factor-continue"
          >
            {enabled ? 'Continue to dashboard' : (mandatory ? 'Enable 2FA to continue' : 'Skip for now')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
